/**
 * @cfleet/api-client - Token Storage
 *
 * Platform-agnostic token storage abstraction for the auth middleware.
 * Provides memory storage by default, with ability to swap implementations.
 *
 * @example
 * // Use default memory storage
 * import { setTokens, clearTokens } from '@cfleet/api-client';
 * setTokens('access', 'refresh', 3600);
 *
 * @example
 * // Use custom storage (e.g., from @cfleet/auth)
 * import { setTokenStorage } from '@cfleet/api-client';
 * import { webStorage } from '@cfleet/auth';
 * setTokenStorage(webStorage);
 */

/**
 * Token data structure stored by the middleware
 */
export interface StoredTokens {
  /** JWT access token */
  accessToken: string;
  /** Refresh token for obtaining new access tokens */
  refreshToken: string;
  /** Token expiry in seconds from time of issue */
  expiresIn: number;
  /** Timestamp (ms) when the access token expires */
  expiresAt: number;
}

/**
 * Token storage interface for platform-agnostic token management.
 * Implementations must be synchronous for simplicity in middleware.
 */
export interface TokenStorageInterface {
  /** Get the current access token, or null if not set */
  getAccessToken(): string | null;

  /** Get the current refresh token, or null if not set */
  getRefreshToken(): string | null;

  /** Get the token expiry timestamp (ms), or null if not set */
  getExpiresAt(): number | null;

  /**
   * Store tokens
   * @param accessToken - JWT access token
   * @param refreshToken - Refresh token
   * @param expiresIn - Token lifetime in seconds
   */
  setTokens(accessToken: string, refreshToken: string, expiresIn: number): void;

  /** Clear all stored tokens */
  clearTokens(): void;

  /** Get current tenant ID for multi-tenant requests */
  getTenantId(): string | null;

  /** Set tenant ID for multi-tenant requests */
  setTenantId(tenantId: string | null): void;
}

/**
 * In-memory token storage implementation.
 *
 * This is the default storage used by the middleware.
 * Tokens are lost on page refresh/app restart, which is secure
 * but requires re-authentication.
 *
 * For persistent storage, configure a different implementation
 * using `setTokenStorage()`.
 */
export class MemoryTokenStorage implements TokenStorageInterface {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt: number | null = null;
  private tenantId: string | null = null;

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getExpiresAt(): number | null {
    return this.expiresAt;
  }

  setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.expiresAt = Date.now() + expiresIn * 1000;
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
  }

  getTenantId(): string | null {
    return this.tenantId;
  }

  setTenantId(tenantId: string | null): void {
    this.tenantId = tenantId;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level storage instance and helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Current token storage instance.
 * Defaults to memory storage, can be changed via setTokenStorage().
 */
let tokenStorage: TokenStorageInterface = new MemoryTokenStorage();

/**
 * Set the token storage implementation.
 *
 * Call this early in your app's initialization to configure storage.
 * For React Native apps, use secure storage. For web apps with
 * persistence needs, use localStorage-based storage.
 *
 * @param storage - The storage implementation to use
 *
 * @example
 * // Use localStorage-based storage from @cfleet/auth
 * import { setTokenStorage } from '@cfleet/api-client';
 * import { createWebTokenStorage } from './my-storage';
 * setTokenStorage(createWebTokenStorage());
 */
export function setTokenStorage(storage: TokenStorageInterface): void {
  tokenStorage = storage;
}

/**
 * Get the current token storage implementation.
 *
 * @returns The current TokenStorageInterface implementation
 */
export function getTokenStorage(): TokenStorageInterface {
  return tokenStorage;
}

/**
 * Reset token storage to the default memory implementation.
 * Useful for testing or when you want to clear all state.
 */
export function resetTokenStorage(): void {
  tokenStorage = new MemoryTokenStorage();
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience functions for direct token manipulation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Store authentication tokens.
 *
 * Call this after successful login to store tokens for automatic
 * attachment to subsequent API requests.
 *
 * @param accessToken - JWT access token
 * @param refreshToken - Refresh token for obtaining new access tokens
 * @param expiresIn - Token lifetime in seconds
 *
 * @example
 * const { data } = await client.POST('/auth/login', { body: credentials });
 * if (data) {
 *   setTokens(data.token, data.refresh_token, data.expires_in);
 * }
 */
export function setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
  tokenStorage.setTokens(accessToken, refreshToken, expiresIn);
}

/**
 * Clear all stored tokens.
 *
 * Call this on logout or when authentication fails to clear
 * all stored tokens and tenant information.
 *
 * @example
 * await client.POST('/auth/logout');
 * clearTokens();
 */
export function clearTokens(): void {
  tokenStorage.clearTokens();
}

/**
 * Get the current access token.
 *
 * @returns The current access token, or null if not authenticated
 */
export function getAccessToken(): string | null {
  return tokenStorage.getAccessToken();
}

/**
 * Get the current refresh token.
 *
 * @returns The current refresh token, or null if not authenticated
 */
export function getRefreshToken(): string | null {
  return tokenStorage.getRefreshToken();
}

/**
 * Check if the user has tokens stored (is potentially authenticated).
 *
 * Note: This does not validate the tokens, only checks if they exist.
 *
 * @returns true if tokens are stored, false otherwise
 */
export function hasTokens(): boolean {
  return tokenStorage.getAccessToken() !== null;
}

/**
 * Check if the access token is expired or about to expire.
 *
 * @param bufferSeconds - Consider token expired if it expires within this many seconds (default: 60)
 * @returns true if token is expired or will expire within buffer, false otherwise
 */
export function isTokenExpired(bufferSeconds: number = 60): boolean {
  const expiresAt = tokenStorage.getExpiresAt();
  if (expiresAt === null) {
    return true;
  }
  return Date.now() >= expiresAt - bufferSeconds * 1000;
}

/**
 * Set the current tenant ID for multi-tenant requests.
 *
 * @param tenantId - The tenant ID to use, or null to clear
 */
export function setTenantId(tenantId: string | null): void {
  tokenStorage.setTenantId(tenantId);
}

/**
 * Get the current tenant ID.
 *
 * @returns The current tenant ID, or null if not set
 */
export function getTenantId(): string | null {
  return tokenStorage.getTenantId();
}
